import { z } from 'zod'
import { type TotemAppSetting, InvoiceEmissionType, TotemSubtype } from '../../models/AppSetting'
import { PeripheralDTO, mapDTOToPeripheral } from '../../../peripherals/gateways/DTOs/PeripheralDTO'
import { mapDTOToServerApp, ServerAppDTO } from './ServerAppDTO'
import { mapDTOToVpnSetting, VpnSettingDTO } from './VpnSettingDTO'
import { mapDTOToSlimModel, SlimModelDTO } from '~/domain/shared/slim/gateways/DTOs/SlimModelDTO'

const TotemSubtypeDTO = z.nativeEnum(TotemSubtype)

const TotemWelcomeScreenSettingDTO = z.object({
  welcome_message: z.string().nullish(),
  finish_message: z.string().nullish()
})

const TotemThemeSettingDTO = z.object({
  primary_color: z.string().optional(),
  secondary_color: z.string().optional()
})

const TotemUseParamsSettingDTO = z.object({
  isRequestTableIdentifier: z.boolean(),
  isHiddenObservation: z.boolean().optional(),
  isHiddenConsumptionLocation: z.boolean(),
  isHiddenSaleNumber: z.boolean(),
  isAcceptDiscountVoucher: z.boolean(),
  isDisplayQuantitySelector: z.boolean(),
  isShowPriceWithDiscount: z.boolean(),
  isEnableTranslate: z.boolean(),
  isRequestCustomerName: z.boolean()
})

const TotemFiscalSettingDTO = z.object({
  isGenerateInvoice: z.boolean(),
  generateInvoiceType: z.nativeEnum(InvoiceEmissionType),
  isDocumentInInvoice: z.boolean(),
  isSendInvoiceToEmail: z.boolean()
})

const TotemPeripheralsSettingDTO = z.object({
  sat_id: z.number().nullish(),
  sat: PeripheralDTO.nullish(),
  print_point_id: z.number().nullish(),
  printPoint: PeripheralDTO.nullish(),
  print_point_expedition_id: z.number().nullish(),
  printPointExpedition: PeripheralDTO.nullish(),
  scale_id: z.number().nullish(),
  scale: PeripheralDTO.nullish()
})

export const TotemAppSettingDTO = z.object({
  server: ServerAppDTO.nullish(),
  server_id: z.number().optional(),
  sub_type: TotemSubtypeDTO,
  self_order: z.boolean().optional(),
  base_product_id: z.number().nullish(),
  base_product: SlimModelDTO.nullish(),
  welcome_screen: TotemWelcomeScreenSettingDTO,
  theme: TotemThemeSettingDTO,
  use_params: TotemUseParamsSettingDTO,
  fiscal: TotemFiscalSettingDTO,
  peripherals: TotemPeripheralsSettingDTO.optional(),
  customization_id: z.number().nullish(),
  customization: SlimModelDTO.nullish(),
  vpn: VpnSettingDTO.nullish(),
  is_generate_offline_sale_number: z.boolean().optional(),
  is_generate_and_print_offline_sale: z.boolean().optional()
})

export type TotemAppSettingDTO = z.infer<typeof TotemAppSettingDTO>

export function mapTotemAppSettingToDTO (model: TotemAppSetting): TotemAppSettingDTO {
  return {
    server_id: model.server?.id,
    sub_type: model.subtype,
    self_order: model.selfOrder,
    base_product_id: model.baseProduct?.id,
    welcome_screen: {
      welcome_message: model.welcomeScreen.welcomeMessage,
      finish_message: model.welcomeScreen.finishMessage
    },
    theme: {
      primary_color: normalizeHexColor(model.theme.primaryColor),
      secondary_color: normalizeHexColor(model.theme.secondaryColor)
    },
    use_params: {
      isRequestTableIdentifier: model.useParams.isRequestTableIdentifier,
      isHiddenObservation: model.useParams.isHiddenObservation,
      isHiddenConsumptionLocation: model.useParams.isHiddenConsumptionLocation,
      isHiddenSaleNumber: model.useParams.isHiddenSaleNumber,
      isAcceptDiscountVoucher: model.useParams.isAcceptDiscountVoucher,
      isDisplayQuantitySelector: model.useParams.isDisplayQuantitySelector,
      isShowPriceWithDiscount: model.useParams.isShowPriceWithDiscount,
      isEnableTranslate: model.useParams.isEnableTranslate,
      isRequestCustomerName: model.useParams.isRequestCustomerName
    },
    fiscal: {
      isGenerateInvoice: model.fiscal.isGenerateInvoice,
      generateInvoiceType: model.fiscal.generateInvoiceType,
      isDocumentInInvoice: model.fiscal.isDocumentInInvoice,
      isSendInvoiceToEmail: model.fiscal.isSendInvoiceToEmail
    },
    peripherals: {
      sat_id: model.peripherals?.sat?.id,
      print_point_id: model.peripherals?.printPoint?.id,
      print_point_expedition_id: model.peripherals?.printPointExpedition?.id,
      scale_id: model.peripherals?.scale?.id
    },
    customization_id: model.customization?.id,
    is_generate_offline_sale_number: model.isGenerateOfflineSaleNumber,
    is_generate_and_print_offline_sale: model.isGenerateAndPrintOfflineSale
  }
}

export function mapDTOToTotemAppSetting (dto: TotemAppSettingDTO): TotemAppSetting {
  return {
    server: dto.server ? mapDTOToServerApp(dto.server) : undefined,
    subtype: dto.sub_type,
    selfOrder: dto.self_order ?? false,
    baseProduct: dto.base_product ? mapDTOToSlimModel(dto.base_product) : undefined,
    welcomeScreen: {
      welcomeMessage: dto.welcome_screen.welcome_message ?? '',
      finishMessage: dto.welcome_screen.finish_message ?? ''
    },
    theme: {
      primaryColor: dto.theme.primary_color,
      secondaryColor: dto.theme.secondary_color
    },
    useParams: {
      isRequestTableIdentifier: dto.use_params.isRequestTableIdentifier,
      isHiddenObservation: dto.use_params.isHiddenObservation ?? false,
      isHiddenConsumptionLocation: dto.use_params.isHiddenConsumptionLocation,
      isHiddenSaleNumber: dto.use_params.isHiddenSaleNumber,
      isAcceptDiscountVoucher: dto.use_params.isAcceptDiscountVoucher,
      isDisplayQuantitySelector: dto.use_params.isDisplayQuantitySelector,
      isShowPriceWithDiscount: dto.use_params.isShowPriceWithDiscount,
      isEnableTranslate: dto.use_params.isEnableTranslate,
      isRequestCustomerName: dto.use_params.isRequestCustomerName
    },
    fiscal: {
      isGenerateInvoice: dto.fiscal.isGenerateInvoice,
      generateInvoiceType: dto.fiscal.generateInvoiceType,
      isDocumentInInvoice: dto.fiscal.isDocumentInInvoice,
      isSendInvoiceToEmail: dto.fiscal.isSendInvoiceToEmail
    },
    peripherals: {
      sat: dto.peripherals?.sat ? mapDTOToPeripheral(dto.peripherals.sat) : undefined,
      printPoint: dto.peripherals?.printPoint ? mapDTOToPeripheral(dto.peripherals.printPoint) : undefined,
      printPointExpedition: dto.peripherals?.printPointExpedition
        ? mapDTOToPeripheral(dto.peripherals.printPointExpedition)
        : undefined,
      scale: dto.peripherals?.scale ? mapDTOToPeripheral(dto.peripherals.scale) : undefined
    },
    customization: dto.customization ? mapDTOToSlimModel(dto.customization) : undefined,
    vpn: dto.vpn ? mapDTOToVpnSetting(dto.vpn) : undefined,
    isGenerateOfflineSaleNumber: dto.is_generate_offline_sale_number,
    isGenerateAndPrintOfflineSale: dto.is_generate_and_print_offline_sale
  }
}

function normalizeHexColor (color: string | undefined): string {
  if (!color) { return '' }
  const cleaned = color.replace('#', '')
  return cleaned ? `#${cleaned}` : ''
}
